// Shared form validation and spam prevention utilities

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
const HTML_REGEX = /<[^>]*>/g;
const URL_SPAM_REGEX = /(https?:\/\/|www\.|\[url|<a\s)/i;

export function isValidEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim().toLowerCase();
  if (trimmed.length > 254) return false;
  if (!EMAIL_REGEX.test(trimmed)) return false;
  // Block disposable email domains commonly used by bots
  const disposable = ['mailinator.com', 'guerrillamail.com', 'tempmail.com', 'throwaway.email', 'yopmail.com', 'sharklasers.com', 'trashmail.com'];
  const domain = trimmed.split('@')[1];
  if (disposable.includes(domain)) return false;
  return true;
}

export function isValidPhone(phone) {
  if (!phone || typeof phone !== 'string') return true; // optional field
  const cleaned = phone.trim();
  if (cleaned === '') return true;
  if (cleaned.length < 7 || cleaned.length > 20) return false;
  return PHONE_REGEX.test(cleaned);
}

export function sanitizeText(text) {
  if (!text || typeof text !== 'string') return '';
  // Strip HTML tags
  return text.replace(HTML_REGEX, '').trim();
}

export function containsHtml(text) {
  if (!text) return false;
  return HTML_REGEX.test(text);
}

export function containsSpamLinks(text) {
  if (!text) return false;
  return URL_SPAM_REGEX.test(text);
}

export function validateFormField(value, type = 'text') {
  const clean = sanitizeText(value);
  
  if (type === 'email') {
    if (!isValidEmail(clean)) return { valid: false, error: 'Please enter a valid email address.' };
  }
  if (type === 'phone') {
    if (!isValidPhone(clean)) return { valid: false, error: 'Please enter a valid phone number.' };
  }
  if (type === 'text' || type === 'message') {
    if (containsHtml(value)) return { valid: false, error: 'HTML is not allowed.' };
    if (containsSpamLinks(value) && type === 'message') {
      // Allow URLs in messages but flag excessive ones
      const urlCount = (value.match(/https?:\/\//g) || []).length;
      if (urlCount > 2) return { valid: false, error: 'Too many links detected.' };
    }
  }
  if (type === 'name') {
    if (containsHtml(value)) return { valid: false, error: 'HTML is not allowed.' };
    if (/[0-9]/.test(clean) && clean.replace(/[^0-9]/g, '').length > 3) return { valid: false, error: 'Please enter a valid name.' };
    if (clean.length < 2) return { valid: false, error: 'Name must be at least 2 characters.' };
    if (clean.length > 100) return { valid: false, error: 'Name is too long.' };
  }
  
  return { valid: true, error: null, value: clean };
}

// Honeypot field name - bots fill this hidden field, humans don't
export const HONEYPOT_FIELD = '_website_url';

export function isHoneypotFilled(value) {
  return value && value.trim().length > 0;
}

// Simple rate limiting (per form, per session)
const submitTimestamps = {};

export function checkRateLimit(formId, minIntervalMs = 3000) {
  const now = Date.now();
  const lastSubmit = submitTimestamps[formId] || 0;
  if (now - lastSubmit < minIntervalMs) {
    return false; // Too fast
  }
  submitTimestamps[formId] = now;
  return true;
}

// Time-based detection: if form submitted in < 2 seconds, likely a bot
const formLoadTimes = {};

export function markFormLoaded(formId) {
  formLoadTimes[formId] = Date.now();
}

export function isSubmitTooFast(formId, minMs = 2000) {
  const loadTime = formLoadTimes[formId];
  if (!loadTime) return false;
  return Date.now() - loadTime < minMs;
}
